export interface SSEEvent {
  type: string;
  content?: string;
}

export type EventHandler = (
  event: SSEEvent,
  options: any,
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (error: string) => void
) => void;

export class SSEEventDispatcher {
  private handlers: Map<string, EventHandler> = new Map();
  // P-F1: 流式标签缓冲 —— 子智能体内容可能跨多个 SSE chunk 到达，
  // 此时起始标签与闭合标签不在同一个 event.content 中，正则匹配会失败。
  private _subThinkingBuffer = '';
  private _subOutputBuffer = '';

  constructor() {
    this.registerDefaultHandlers();
  }

  private registerDefaultHandlers(): void {
    this.register('replace', this.handleReplace);
    this.register('text', this.handleText);
    this.register('thinking', this.handleThinking);
    this.register('status', this.handleStatus);
    this.register('intent', this.handleIntent);
    this.register('message_ids', this.handleMessageIds);
    this.register('agent_id', this.handleAgentId);
    this.register('collab_status', this.handleCollabStatus);
    this.register('sub_thinking', this.handleSubThinking);
    this.register('sub_output', this.handleSubOutput);
    this.register('clarification', this.handleClarification);
    this.register('generation_confirm', this.handleGenerationConfirm);
    this.register('search_result', this.handleSearchResult);
    this.register('heartbeat', () => {});
    this.register('error', this.handleError);
    this.register('done', this.handleDone);
  }

  register(eventType: string, handler: EventHandler): void {
    this.handlers.set(eventType, handler);
  }

  dispatch(
    event: SSEEvent,
    options: any,
    onChunk: (text: string) => void,
    onDone: () => void,
    onError: (error: string) => void
  ): void {
    const handler = this.handlers.get(event.type);
    if (handler) {
      handler(event, options, onChunk, onDone, onError);
    } else {
      // A3: 未知事件类型只记录调试警告，不报错。便于排查后端新增事件后
      // 前端忘记注册 handler 的情况。
      console.warn(
        `[SSEEventDispatcher] Unknown SSE event type: "${event.type}", ` +
        `registered events: [${Array.from(this.handlers.keys()).join(', ')}]`
      );
    }
  }

  private handleReplace: EventHandler = (event, _, onChunk) => {
    onChunk('__REPLACE__');
  };

  private handleText: EventHandler = (event, _, onChunk) => {
    if (event.content) {
      onChunk(event.content);
    }
  };

  private handleThinking: EventHandler = (event, options) => {
    if (event.content) {
      options?.onThinking?.(event.content);
    }
  };

  private handleStatus: EventHandler = (event, options) => {
    if (event.content) {
      options?.onStatus?.(event.content);
    }
  };

  private handleIntent: EventHandler = (event, options) => {
    if (!event.content) return;

    try {
      const content = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onIntent?.(content);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse intent content:', e);
    }
  };

  private handleMessageIds: EventHandler = (event, options) => {
    if (!event.content) return;

    try {
      const content = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content || '{}');
      options?.onMessageIds?.(content);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse message_ids content:', e);
    }
  };

  private handleAgentId: EventHandler = (event, options) => {
    if (event.content) {
      options?.onAgentId?.(event.content);
    }
  };

  private handleCollabStatus: EventHandler = (event, options) => {
    if (event.content) {
      options?.onCollabStatus?.(event.content);
    }
  };

  /**
   * P-F1: 流式标签解析通用方法。
   * 思路：
   *   1) 先与缓冲区已有内容合并；
   *   2) 从头查找首个 [TAG:xxx] 起始标签；
   *   3) 在合并内容中查找配对的 [/TAG:xxx] 闭合标签；
   *   4) 找到则提取并回调，剩余未闭合部分回到缓冲区等待后续 chunk。
   */
  private _handleBufferedTag(
    tagName: 'SUB_THINKING' | 'SUB_OUTPUT',
    bufferField: '_subThinkingBuffer' | '_subOutputBuffer',
    content: string,
    callback: (agentId: string, body: string) => void,
  ): void {
    const text = (this[bufferField] + content).replace(/\r/g, '');
    const openRe = new RegExp(`\\[${tagName}:([^\\]]+)\\]`);
    const closeTagHead = `[/${tagName}:`;

    let remaining = text;

    // 循环：一次 chunk 可能含多对标签
    while (remaining.length > 0) {
      const openMatch = remaining.match(openRe);
      if (!openMatch || openMatch.index === undefined) {
        // 没有起始标签：如果 buffer 之前有遗留，说明标签被截断等待；
        // 如果 buffer 原本为空，说明这段内容不包含目标标签，整体丢弃。
        this[bufferField] = remaining.includes(`[${tagName}:`) ? remaining : '';
        return;
      }

      const agentId = openMatch[1];
      const bodyStart = openMatch.index + openMatch[0].length;
      const closeSearchFrom = remaining.indexOf(closeTagHead + agentId + ']', bodyStart);

      if (closeSearchFrom === -1) {
        // 有起始但无闭合：整段内容进入缓冲区，等下一个 chunk 到齐后再处理
        this[bufferField] = remaining;
        return;
      }

      const body = remaining.slice(bodyStart, closeSearchFrom);
      callback(agentId, body);

      // 移动 remaining 到闭合标签之后
      const closeEnd = closeSearchFrom + (closeTagHead + agentId + ']').length;
      remaining = remaining.slice(closeEnd);
    }

    this[bufferField] = '';
  }

  private handleSubThinking: EventHandler = (event, options) => {
    if (!event.content) return;
    this._handleBufferedTag(
      'SUB_THINKING',
      '_subThinkingBuffer',
      event.content,
      (agentId, body) => options?.onSubThinking?.(agentId, body),
    );
  };

  private handleSubOutput: EventHandler = (event, options) => {
    if (!event.content) return;
    this._handleBufferedTag(
      'SUB_OUTPUT',
      '_subOutputBuffer',
      event.content,
      (agentId, body) => options?.onSubOutput?.(agentId, body),
    );
  };

  private handleClarification: EventHandler = (event, options) => {
    if (!event.content) return;

    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onClarificationRequest?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse clarification content:', e);
    }
  };

  private handleGenerationConfirm: EventHandler = (event, options) => {
    if (!event.content) return;

    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onGenerationConfirm?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse generation_confirm content:', e, event.content);
    }
  };

  private handleSearchResult: EventHandler = (event, options) => {
    if (!event.content) return;

    try {
      const data = typeof event.content === 'object'
        ? event.content
        : JSON.parse(event.content);
      options?.onSearchResult?.(data);
    } catch (e) {
      console.error('[SSEEventDispatcher] Failed to parse search_result content:', e);
    }
  };

  private handleError: EventHandler = (event, _, __, ___, onError) => {
    onError(event.content || 'Unknown error');
  };

  private handleDone: EventHandler = (_, __, ___, onDone) => {
    onDone();
  };
}

// 创建全局实例
export const sseEventDispatcher = new SSEEventDispatcher();
